/**
 * Hook para llamadas WebRTC con Telnyx desde el navegador del agente.
 *
 * Máquina de estados:
 *   idle → requesting_mic → fetching_token → connecting → ringing → active → done
 *                                                                           ↘ error
 *
 * Uso:
 *   const webrtc = useTelnyxWebRTC();
 *   await webrtc.startCall('+34600123456');
 *   webrtc.hangup();
 */

import { useState, useCallback, useRef, useEffect } from 'react';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — tipos de @telnyx/webrtc pueden no ser perfectos en TS estricto
import { TelnyxRTC } from '@telnyx/webrtc';
import { fetchTelnyxWebRTCToken, invalidateTelnyxTokenCache } from '@/services/telnyxWebRTCService';
import { normalizePhoneE164 } from '@/utils/phoneUtils';
import { logger } from '@/utils/logger';

// ─── Tipos ────────────────────────────────────────────────────────────────────

export type WebRTCCallState =
  | 'idle'
  | 'requesting_mic'   // solicitando permiso de micrófono
  | 'fetching_token'   // obteniendo token del backend
  | 'connecting'       // TelnyxRTC conectando al servidor SIP
  | 'ringing'          // llamada saliente sonando
  | 'active'           // llamada activa (voz conectada)
  | 'hanging_up'       // colgando
  | 'reconnecting'     // socket caído, reintentando conexión SIP automáticamente
  | 'done'             // llamada terminada (local o remota)
  | 'error';

export type MicPermission = 'unknown' | 'granted' | 'denied';

export interface UseTelnyxWebRTCReturn {
  callState: WebRTCCallState;
  micPermission: MicPermission;
  errorMessage: string | null;
  /** MediaStream remoto del peer (disponible cuando callState === 'active') */
  remoteStream: MediaStream | null;
  /** true cuando el micrófono está silenciado */
  isMuted: boolean;
  startCall: (phone: string) => Promise<void>;
  hangup: () => void;
  reset: () => void;
  /** Silenciar / activar micrófono durante la llamada */
  toggleMute: () => void;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useTelnyxWebRTC(): UseTelnyxWebRTCReturn {
  const [callState, setCallState] = useState<WebRTCCallState>('idle');
  const [micPermission, setMicPermission] = useState<MicPermission>('unknown');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  const [isMuted, setIsMuted] = useState(false);

  const clientRef = useRef<any>(null);
  const activeCallRef = useRef<any>(null);
  /** Audio element oculto para reproducir el audio entrante del peer */
  const audioPlaybackRef = useRef<HTMLAudioElement | null>(null);
  /** Guard anti-reentrada: el SDK de Telnyx emite notificaciones dentro de hangup/disconnect
   *  que a su vez llamarían a _disconnectClient → stack overflow. */
  const isDisconnectingRef = useRef(false);
  /** Timer de refresh proactivo de token (se dispara a los expires_in - 5 min). */
  const tokenRefreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Timer de backoff para reconexión automática de socket. */
  const socketReconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Número de intentos de reconexión desde la última llamada exitosa. */
  const reconnectAttemptsRef = useRef(0);
  /** caller_id_number del último token — necesario para reconexión. */
  const callerIdRef = useRef<string | null>(null);

  const RECONNECT_DELAYS_MS = [1_000, 2_000, 5_000, 10_000, 30_000] as const;
  const MAX_RECONNECT_ATTEMPTS = RECONNECT_DELAYS_MS.length;

  // Cleanup al desmontar
  useEffect(() => {
    return () => {
      _disconnectClient();
    };
  }, []);

  /**
   * Crea (o reutiliza) el audio element oculto para reproducción.
   * Se llama ANTES de conectar el cliente TelnyxRTC para que el SDK lo use
   * como remoteElement → reproduce tonos de marcación, ringback y voz.
   */
  function _ensureAudioElement(): HTMLAudioElement {
    if (!audioPlaybackRef.current) {
      const el = document.createElement('audio');
      el.autoplay = true;
      el.setAttribute('playsinline', '');
      el.style.display = 'none';
      document.body.appendChild(el);
      audioPlaybackRef.current = el;
      logger.info('[useTelnyxWebRTC] 🔊 Audio element creado para reproducción');
    }
    return audioPlaybackRef.current;
  }

  const remoteStreamRetryRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Extrae el remoteStream del call object de Telnyx.
   * Si no está disponible inmediatamente, reintenta hasta 10 veces (cada 200ms).
   * TelnyxRTC a veces provee el stream unos ms después del estado 'active'.
   */
  function _extractRemoteStream(call: any, attempt = 0): void {
    // Intentar obtener el stream
    const stream: MediaStream | undefined =
      call.remoteStream ??
      call.options?.remoteStream ??
      call.peer?.remoteStream;

    if (stream instanceof MediaStream && stream.active && stream.getAudioTracks().length > 0) {
      logger.info(
        `[useTelnyxWebRTC] ✅ remoteStream capturado (intento ${attempt}): ` +
        `tracks=${stream.getAudioTracks().length}, active=${stream.active}, ` +
        `trackEnabled=${stream.getAudioTracks()[0]?.enabled}, ` +
        `trackState=${stream.getAudioTracks()[0]?.readyState}`
      );
      setRemoteStream(stream);
      return;
    }

    // Retry hasta 10 veces (2 segundos total)
    if (attempt < 10) {
      logger.info(`[useTelnyxWebRTC] ⏳ remoteStream no disponible (intento ${attempt}), reintentando en 200ms...`);
      remoteStreamRetryRef.current = setTimeout(() => {
        // Verificar que la llamada sigue activa
        if (activeCallRef.current === call) {
          _extractRemoteStream(call, attempt + 1);
        }
      }, 200);
    } else {
      logger.warn('[useTelnyxWebRTC] ❌ remoteStream no disponible tras 10 intentos — solo se transcribirá el agente');
    }
  }

  /**
   * Programa un refresh proactivo del token ~5 min antes de que expire.
   * Opera en background sin interrumpir la llamada activa.
   * Se auto-reprograma con el nuevo expires_in tras cada refresh exitoso.
   */
  function _scheduleTokenRefresh(expiresIn: number) {
    if (tokenRefreshTimerRef.current) clearTimeout(tokenRefreshTimerRef.current);
    const MARGIN_S = 300; // 5 min de margen
    const delayMs = Math.max(0, (expiresIn - MARGIN_S) * 1000);
    if (delayMs === 0) return; // Token ya casi vencido

    tokenRefreshTimerRef.current = setTimeout(async () => {
      logger.info('[useTelnyxWebRTC] 🔄 Refresh proactivo de token (background)...');
      try {
        invalidateTelnyxTokenCache();
        const fresh = await fetchTelnyxWebRTCToken();
        callerIdRef.current = fresh.caller_id_number ?? callerIdRef.current;
        logger.info(`[useTelnyxWebRTC] ✅ Token refrescado. Nuevo expires_in: ${fresh.expires_in}s`);
        _scheduleTokenRefresh(fresh.expires_in);
      } catch (err) {
        logger.warn('[useTelnyxWebRTC] ⚠️ No se pudo refrescar token — reintentando en 60s:', err);
        tokenRefreshTimerRef.current = setTimeout(async () => {
          try {
            invalidateTelnyxTokenCache();
            const fresh = await fetchTelnyxWebRTCToken();
            callerIdRef.current = fresh.caller_id_number ?? callerIdRef.current;
            _scheduleTokenRefresh(fresh.expires_in);
          } catch { /* Si falla de nuevo, el próximo startCall lo resolverá */ }
        }, 60_000);
      }
    }, delayMs);

    const refreshAt = new Date(Date.now() + delayMs);
    logger.info(`[useTelnyxWebRTC] ⏰ Token refresh programado en ${Math.round(delayMs / 60_000)}min (${refreshAt.toLocaleTimeString()})`);
  }

  /**
   * Intenta reconectar el WebSocket SIP con backoff exponencial.
   * Se activa cuando el socket cae inesperadamente (no por hangup/reset).
   * Si tiene éxito, la llamada anterior ya está muerta (Telnyx la finalizó);
   * el agente queda con SIP registrado listo para volver a marcar.
   */
  function _attemptSocketReconnect() {
    const attempt = reconnectAttemptsRef.current;
    if (attempt >= MAX_RECONNECT_ATTEMPTS) {
      logger.error('[useTelnyxWebRTC] ❌ Máximo de reconexiones alcanzado');
      setCallState('error');
      setErrorMessage('Conexión perdida con Telnyx tras varios intentos. Recarga la página.');
      return;
    }

    const delay = RECONNECT_DELAYS_MS[attempt] ?? 30_000;
    reconnectAttemptsRef.current = attempt + 1;
    setCallState('reconnecting');
    logger.warn(`[useTelnyxWebRTC] 🔄 Reconectando socket SIP (intento ${attempt + 1}/${MAX_RECONNECT_ATTEMPTS}) en ${delay}ms`);

    socketReconnectTimerRef.current = setTimeout(async () => {
      try {
        // Limpiar cliente viejo. Null ANTES de disconnect para que el evento
        // telnyx.socket.close del cliente viejo no dispare otra reconexión.
        const oldClient = clientRef.current;
        clientRef.current = null;
        if (oldClient) {
          try { oldClient.disconnect(); } catch { /* ignorar */ }
        }

        // Token fresco (el viejo puede haber expirado en llamadas >1h)
        invalidateTelnyxTokenCache();
        const tokenResp = await fetchTelnyxWebRTCToken();
        callerIdRef.current = tokenResp.caller_id_number ?? callerIdRef.current;

        const remoteAudioEl = _ensureAudioElement();
        const newClient = new TelnyxRTC({
          login_token: tokenResp.token,
          remoteElement: remoteAudioEl,
        } as any);
        clientRef.current = newClient;

        newClient.on('telnyx.ready', () => {
          logger.info('[useTelnyxWebRTC] ✅ Socket SIP reconectado. La llamada anterior fue terminada por Telnyx.');
          reconnectAttemptsRef.current = 0;
          _scheduleTokenRefresh(tokenResp.expires_in);
          // La llamada PSTN ya fue finalizada por Telnyx al caer el socket.
          // Cambiamos a 'done' para que la UI ofrezca volver a marcar.
          setCallState('done');
          setErrorMessage('La llamada se cortó por pérdida de conexión. Puedes volver a marcar.');
        });

        newClient.on('telnyx.socket.close', () => {
          if (!clientRef.current) return;
          logger.warn('[useTelnyxWebRTC] 🔌 Socket caído durante reconexión');
          _attemptSocketReconnect();
        });

        newClient.on('telnyx.error', (err: any) => {
          logger.error('[useTelnyxWebRTC] Error durante reconexión SIP:', err);
          _attemptSocketReconnect();
        });

        newClient.connect();
      } catch (err) {
        logger.error('[useTelnyxWebRTC] Error al intentar reconexión:', err);
        _attemptSocketReconnect();
      }
    }, delay);
  }

  function _disconnectClient() {
    // Reentrada: el SDK emite notificaciones (state=destroy/purge) durante hangup()
    // y disconnect() que vuelven a llamar a esta función → recursión infinita.
    if (isDisconnectingRef.current) return;
    isDisconnectingRef.current = true;
    try {
      // Cancelar todos los timers pendientes
      if (remoteStreamRetryRef.current) {
        clearTimeout(remoteStreamRetryRef.current);
        remoteStreamRetryRef.current = null;
      }
      if (hangupTimeoutRef.current) {
        clearTimeout(hangupTimeoutRef.current);
        hangupTimeoutRef.current = null;
      }
      if (tokenRefreshTimerRef.current) {
        clearTimeout(tokenRefreshTimerRef.current);
        tokenRefreshTimerRef.current = null;
      }
      if (socketReconnectTimerRef.current) {
        clearTimeout(socketReconnectTimerRef.current);
        socketReconnectTimerRef.current = null;
      }
      reconnectAttemptsRef.current = 0;
      // IMPORTANTE: vaciar refs ANTES de invocar al SDK. Así, si el SDK dispara
      // un evento síncrono que re-entra en esta función o en hangup(), los refs
      // ya están a null y no se vuelve a colgar/desconectar la misma instancia.
      const call = activeCallRef.current;
      activeCallRef.current = null;
      if (call) {
        try { call.hangup?.(); } catch { /* ignorar */ }
        try { call.bye?.(); } catch { /* ignorar */ }
      }
      const client = clientRef.current;
      clientRef.current = null;
      if (client) {
        try { client.disconnect(); } catch { /* ignorar */ }
      }
      // Limpiar audio element de reproducción
      if (audioPlaybackRef.current) {
        audioPlaybackRef.current.srcObject = null;
        audioPlaybackRef.current.remove();
        audioPlaybackRef.current = null;
      }
      setRemoteStream(null);
    } finally {
      isDisconnectingRef.current = false;
    }
  }

  const toggleMute = useCallback(() => {
    const call = activeCallRef.current;
    if (!call) return;
    try {
      if (isMuted) {
        call.unmuteAudio?.() ?? call.unmute?.();
      } else {
        call.muteAudio?.() ?? call.mute?.();
      }
      setIsMuted(!isMuted);
      logger.info(`[useTelnyxWebRTC] Micrófono ${isMuted ? 'activado' : 'silenciado'}`);
    } catch (e) {
      logger.error('[useTelnyxWebRTC] Error al cambiar mute:', e);
    }
  }, [isMuted]);

  const hangupTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const hangup = useCallback(() => {
    // No reentrar si ya estamos desconectando (evita que callbacks síncronos
    // del SDK disparen un segundo hangup en cascada).
    if (isDisconnectingRef.current) return;
    setCallState('hanging_up');
    try {
      const call = activeCallRef.current;
      if (call) {
        // Intentar todos los métodos de cuelgue del SDK
        logger.info('[useTelnyxWebRTC] Colgando llamada...');
        call.hangup?.();
        call.bye?.();
        call.disconnect?.();

        // Safety: si el SDK no emite 'hangup'/'destroy' en 3s, forzar desconexión
        hangupTimeoutRef.current = setTimeout(() => {
          logger.warn('[useTelnyxWebRTC] ⚠️ Timeout colgando — forzando desconexión');
          _disconnectClient();
          setCallState('done');
        }, 3000);
      } else {
        _disconnectClient();
        setCallState('done');
      }
    } catch (e) {
      logger.error('[useTelnyxWebRTC] Error al colgar:', e);
      _disconnectClient();
      setCallState('done');
    }
  }, []);

  const reset = useCallback(() => {
    _disconnectClient();
    setCallState('idle');
    setErrorMessage(null);
    setIsMuted(false);
  }, []);

  const startCall = useCallback(async (rawPhone: string) => {
    const phone = normalizePhoneE164(rawPhone);
    logger.info('[useTelnyxWebRTC] startCall — raw:', rawPhone, '→ normalizado:', phone);
    try {
      // ── 1. Verificación de micrófono (sin getUserMedia) ────────────────────
      // IMPORTANTE: NO hacemos getUserMedia aquí. Lo hacía antes como "pre-check"
      // pero causaba race conditions: al liberar el stream inmediatamente,
      // TelnyxRTC a veces encontraba el dispositivo "ocupado" brevemente
      // (NotReadableError en Windows). En su lugar:
      //   a) Permissions API → detectar denegación explícita
      //   b) enumerateDevices → verificar que existe un micrófono
      //   c) TelnyxRTC maneja su propio getUserMedia al hacer newCall()
      setCallState('requesting_mic');
      setErrorMessage(null);

      // a) Verificar permiso con Permissions API (no invasivo, no pide prompt)
      try {
        if (navigator.permissions?.query) {
          const permStatus = await navigator.permissions.query({ name: 'microphone' as PermissionName });
          logger.info('[useTelnyxWebRTC] Permissions API state:', permStatus.state);
          if (permStatus.state === 'denied') {
            setMicPermission('denied');
            setCallState('error');
            setErrorMessage(
              'Permiso de micrófono denegado. Verifica:\n1. Navegador: clic en 🔒 → Permisos → Micrófono → Permitir\n2. Windows: Configuración → Privacidad → Micrófono → activar para este navegador\nLuego recarga la página.'
            );
            return;
          }
          if (permStatus.state === 'granted') {
            setMicPermission('granted');
          }
        }
      } catch {
        logger.warn('[useTelnyxWebRTC] Permissions API no disponible');
      }

      // b) Verificar que hay al menos un micrófono conectado
      try {
        if (navigator.mediaDevices?.enumerateDevices) {
          const devices = await navigator.mediaDevices.enumerateDevices();
          const hasMic = devices.some((d) => d.kind === 'audioinput');
          if (!hasMic) {
            setCallState('error');
            setErrorMessage(
              'No se encontró ningún micrófono. Conecta un micrófono o auriculares y vuelve a intentarlo.'
            );
            return;
          }
          logger.info('[useTelnyxWebRTC] Micrófono detectado:', devices.filter((d) => d.kind === 'audioinput').length, 'dispositivo(s)');
        }
      } catch {
        logger.warn('[useTelnyxWebRTC] enumerateDevices no disponible');
      }

      // c) Verificar contexto seguro (HTTPS o localhost)
      if (!window.isSecureContext) {
        setCallState('error');
        setErrorMessage(
          'WebRTC requiere conexión segura (HTTPS). No se puede acceder al micrófono desde HTTP.'
        );
        return;
      }

      // ── 2. Token del backend ────────────────────────────────────────────────
      // fetchTelnyxWebRTCToken() usa caché de 1h → instantáneo si ya fue precargado.
      // El servicio maneja retry y deduplicación internamente.
      setCallState('fetching_token');
      reconnectAttemptsRef.current = 0; // nueva llamada = presupuesto de reconexión limpio
      const tokenResp = await fetchTelnyxWebRTCToken();
      const { token, caller_id_number } = tokenResp;
      callerIdRef.current = caller_id_number ?? null;
      logger.info('[useTelnyxWebRTC] Token obtenido. caller_id_number:', caller_id_number, '| credential_id:', tokenResp.credential_id);
      // Programar refresh proactivo del token antes de que expire (~55 min si expires_in=3600)
      _scheduleTokenRefresh(tokenResp.expires_in);

      // ── 3. Crear audio element y cliente TelnyxRTC ─────────────────────────
      setCallState('connecting');
      _disconnectClient();

      // Crear audio element ANTES de conectar → el SDK lo usa para TODO el audio:
      // tonos de marcación, ringback, early media y voz del interlocutor.
      const remoteAudioEl = _ensureAudioElement();

      // remoteElement le dice al SDK dónde reproducir el audio remoto.
      // Los tipos de @telnyx/webrtc no incluyen remoteElement en IClientOptions,
      // pero el SDK lo acepta en runtime (documentado en su README).
      const client = new TelnyxRTC({
        login_token: token,
        remoteElement: remoteAudioEl,
      } as any);
      clientRef.current = client;

      // ── 4. Eventos del cliente ──────────────────────────────────────────────

      // Socket cerrado inesperadamente (no por hangup/reset intencional):
      // clientRef.current es null solo cuando _disconnectClient() fue llamado deliberadamente.
      client.on('telnyx.socket.close', () => {
        if (!clientRef.current) return; // desconexión intencional — no reconectar
        logger.warn('[useTelnyxWebRTC] 🔌 Socket Telnyx cerrado inesperadamente — iniciando reconexión');
        _attemptSocketReconnect();
      });

      client.on('telnyx.ready', () => {
        reconnectAttemptsRef.current = 0; // SIP registrado con éxito → resetear contador
        logger.info('[useTelnyxWebRTC] Cliente SIP registrado. Llamando a:', phone);
        try {
          const callOptions: Record<string, unknown> = {
            destinationNumber: phone,
            audio: true,
            video: false,
            // remoteElement también en la llamada para máxima compatibilidad
            remoteElement: remoteAudioEl,
          };
          // callerNumber es obligatorio para llamadas PSTN salientes.
          // Sin él Telnyx no puede asociar un número origen y rechaza la llamada.
          // NOTA: El SDK @telnyx/webrtc usa "callerNumber" (no "callerIdNumber").
          if (caller_id_number) {
            callOptions.callerNumber = caller_id_number;
            logger.info('[useTelnyxWebRTC] callerNumber:', caller_id_number);
          } else {
            logger.warn('[useTelnyxWebRTC] caller_id_number no disponible — las llamadas PSTN pueden fallar');
          }
          logger.info('[useTelnyxWebRTC] newCall options:', JSON.stringify({...callOptions, remoteElement: '[HTMLAudioElement]'}));
          const call = client.newCall(callOptions);
          activeCallRef.current = call;
        } catch (callErr: any) {
          logger.error('[useTelnyxWebRTC] Error al crear llamada:', callErr);
          setCallState('error');
          setErrorMessage(callErr?.message ?? 'Error al iniciar la llamada');
        }
      });

      client.on('telnyx.notification', (notification: any) => {
        const call = notification?.call;
        if (!call) return;

        const state: string = call.state ?? '';
        logger.info('[useTelnyxWebRTC] Estado de llamada Telnyx:', state);

        switch (state) {
          case 'new':
          case 'requesting':
          case 'trying':
            setCallState('connecting');
            break;

          case 'ringing':
          case 'early':
            setCallState('ringing');
            // Forzar play para ringback/early media (tonos de espera)
            if (audioPlaybackRef.current) {
              audioPlaybackRef.current.play().catch(() => {});
            }
            break;

          case 'active':
            setCallState('active');
            activeCallRef.current = call;
            // Forzar reproducción del audio remoto (por si autoplay fue bloqueado)
            if (audioPlaybackRef.current) {
              audioPlaybackRef.current.play().catch((e) => {
                logger.warn('[useTelnyxWebRTC] ⚠️ No se pudo reproducir audio automáticamente:', e);
              });
            }
            // Extraer remoteStream del call object para transcripción
            _extractRemoteStream(call);
            break;

          case 'hangup':
          case 'destroy':
          case 'purge':
            // Cancelar timeout de cuelgue forzado (el SDK respondió a tiempo)
            if (hangupTimeoutRef.current) {
              clearTimeout(hangupTimeoutRef.current);
              hangupTimeoutRef.current = null;
            }
            activeCallRef.current = null;
            setCallState('done');
            // Desconectar cliente inmediatamente para cortar toda conexión
            _disconnectClient();
            break;

          default:
            logger.info('[useTelnyxWebRTC] Estado no manejado:', state);
        }
      });

      client.on('telnyx.error', (error: any) => {
        logger.error('[useTelnyxWebRTC] Error WebRTC:', error);
        setCallState('error');

        // Interpretar errores comunes del SDK TelnyxRTC
        const msg = String(error?.message ?? '');
        if (msg.includes('Permission') || msg.includes('NotAllowed') || msg.includes('permission')) {
          setMicPermission('denied');
          setErrorMessage(
            'Permiso de micrófono denegado. Verifica:\n1. Navegador: clic en 🔒 → Permisos → Micrófono → Permitir\n2. Windows: Configuración → Privacidad → Micrófono → activar para este navegador\nLuego recarga la página.'
          );
        } else if (msg.includes('NotFound') || msg.includes('Requested device not found')) {
          setErrorMessage(
            'No se encontró ningún micrófono. Conecta un micrófono o auriculares y vuelve a intentarlo.'
          );
        } else if (msg.includes('NotReadable') || msg.includes('Could not start')) {
          setErrorMessage(
            'El micrófono está en uso por otra aplicación o no se puede acceder. Cierra otras apps que lo usen (Teams, Zoom, etc.) y reintenta.'
          );
        } else {
          setErrorMessage(error?.message ?? 'Error en la conexión WebRTC de Telnyx');
        }
      });

      // ── 5. Conectar ─────────────────────────────────────────────────────────
      client.connect();
    } catch (err: any) {
      logger.error('[useTelnyxWebRTC] Error general al iniciar llamada WebRTC:', err);
      setCallState('error');
      const isTimeout =
        err?.code === 'ECONNABORTED' ||
        String(err?.message ?? '').includes('timeout');
      setErrorMessage(
        isTimeout
          ? 'El servidor no responde. Espera unos segundos e inténtalo de nuevo.'
          : (err?.message ?? 'No se pudo iniciar la llamada WebRTC'),
      );
    }
  }, []);

  return {
    callState,
    micPermission,
    errorMessage,
    remoteStream,
    isMuted,
    startCall,
    hangup,
    reset,
    toggleMute,
  };
}
